import * as colors from "./colors.less";

export const commonRedColor = colors.aggressive;
export const commonBlueColor = colors.positive;
export const commonGrayColor = "#F1F1F1";

export const genericColors = {
    red: {
        dark: "#DB0800",
        standard: "#F34C41",
        light: "#FD8E86",
        background: "#FAE1E1",
    },
    yellow: {
        dark: "#DBBE00",
        standard: "#F7DA00",
        light: "#F0E173",
        background: "#F5F0CE",
    },
    orange: {
        dark: "#FF7600",
        standard: "#FF9932",
        light: "#FFC08A",
        background: "#FAE6D7",
    },
    green: {
        dark: "#13AF1A",
        standard: "#72C45D",
        light: "#B3E1AF",
        background: "#DAF7D7",
    },
    teal: {
        dark: "#01A0B9",
        standard: "#00CAD1",
        light: "#8BDCDF",
        background: "#D5F6F7",
    },
    blue: {
        dark: "#1C50E5",
        standard: "#0083FC",
        light: colors.genericBlueLight,
        background: colors.genericBlueBackground,
    },
    pink: {
        dark: "#D23E77",
        standard: "#ED3A90",
        light: "#FFAFC4",
        background: "#FCDCEE",
    },
    violet: {
        dark: "#7C1CDE",
        standard: "#A85BFF",
        light: "#D4ADFF",
        background: "#E8DEFF",
    },
} as const;

/**
 * Used in StoryBook as well.
 * See src/stories/basics/colors/ColorsBasic.stories.mdx
 */
export const basicColors = {
    primary: {
        main: colors.main,
    },
    secondary: {
        main: colors.basicSecondary,
    },
    light: {
        main: colors.basicLight,
    },
    background: {
        main: colors.white,
        hover: "rgba(0, 0, 0, 0.07)", // @var-background-hover-color
        active: "rgba(0, 0, 0, 0.13)", // @var-background-active-color
        action: "rgba(0, 0, 0, 0.21)", // @var-background-action-color
    },
    headerBackground: {
        main: "#F7F7FC", // @var-background-header-color
    },
} as const;

export const semanticColors = {
    neutral: {
        text: {
            primary: colors.neutralTextPrimary,
            primaryDark: colors.neutralTextPrimaryDark,
            secondary: colors.neutralTextSecondary,
            secondaryDark: colors.neutralTextSecondaryDark,
            tertiary: colors.neutralTextTertiary,
            tertiaryDark: colors.neutralTextTertiaryDark,
            quaternary: colors.neutralTextQuaternary,
            quaternaryDark: colors.neutralTextQuaternaryDark,
        },
        background: {
            primary: colors.neutralBackgroundPrimary,
            primaryDark: colors.neutralBackgroundPrimaryDark,
            secondary: colors.neutralBackgroundSecondary,
            secondaryDark: colors.neutralBackgroundSecondaryDark,
            tertiary: colors.neutralBackgroundTertiary,
            tertiaryDark: colors.neutralBackgroundTertiaryDark,
            quaternary: colors.neutralBackgroundQuaternary,
            quaternaryDark: colors.neutralBackgroundQuaternaryDark,
            hover: colors.neutralBackgroundHover,
            hoverDark: colors.neutralBackgroundHoverDark,
            active: colors.neutralBackgroundActive,
            activeDark: colors.neutralBackgroundActiveDark,
            overlay: colors.neutralBackgroundOverlay,
            overlayDark: colors.neutralBackgroundOverlayDark,
        },
        icon: {
            primary: colors.neutralIconPrimary,
            primaryDark: colors.neutralIconPrimaryDark,
            secondary: colors.neutralIconSecondary,
            secondaryDark: colors.neutralIconSecondaryDark,
            tertiary: colors.neutralIconTertiary,
            tertiaryDark: colors.neutralIconTertiaryDark,
            quaternary: colors.neutralIconQuaternary,
            quaternaryDark: colors.neutralIconQuaternaryDark,
        },
        border: {
            primary: colors.neutralBorderPrimary,
            primaryDark: colors.neutralBorderPrimaryDark,
            secondary: colors.neutralBorderSecondary,
            secondaryDark: colors.neutralBorderSecondaryDark,
            tertiary: colors.neutralBorderTertiary,
            tertiaryDark: colors.neutralBorderTertiaryDark,
            quaternary: colors.neutralBorderQuaternary,
            quaternaryDark: colors.neutralBorderQuaternaryDark,
        },
    },
    link: {
        default: colors.linkDefault,
        defaultDark: colors.linkDefaultDark,
        hover: colors.linkHover,
        hoverDark: colors.linkHoverDark,
        active: colors.linkActive,
        activeDark: colors.linkActiveDark,
    },
    success: {
        background: {
            default: colors.successBackgroundDefault,
            defaultDark: colors.successBackgroundDefaultDark,
            hover: colors.successBackgroundHover,
            hoverDark: colors.successBackgroundHoverDark,
            active: colors.successBackgroundActive,
            activeDark: colors.successBackgroundActiveDark,
        },
        text: {
            medium: colors.successTextMedium,
            mediumDark: colors.successTextMediumDark,
            bold: colors.successTextBold,
            boldDark: colors.successTextBoldDark,
        },
        icon: {
            light: colors.successIconLight,
            lightDark: colors.successIconLightDark,
            medium: colors.successIconMedium,
            mediumDark: colors.successIconMediumDark,
            bold: colors.successIconBold,
            boldDark: colors.successIconBoldDark,
        },
        border: {
            light: colors.successBorderLight,
            lightDark: colors.successBorderLightDark,
            medium: colors.successBorderMedium,
            mediumDark: colors.successBorderMediumDark,
            bold: colors.successBorderBold,
            boldDark: colors.successBorderBoldDark,
        },
    },
    warning: {
        background: {
            default: colors.warningBackgroundDefault,
            defaultDark: colors.warningBackgroundDefaultDark,
        },
        text: {
            medium: colors.warningTextMedium,
            mediumDark: colors.warningTextMediumDark,
            bold: colors.warningTextBold,
            boldDark: colors.warningTextBoldDark,
        },
        icon: {
            light: colors.warningIconLight,
            lightDark: colors.warningIconLightDark,
            medium: colors.warningIconMedium,
            mediumDark: colors.warningIconMediumDark,
            bold: colors.warningIconBold,
            boldDark: colors.warningIconBoldDark,
        },
        border: {
            light: colors.warningBorderLight,
            lightDark: colors.warningBorderLightDark,
            medium: colors.warningBorderMedium,
            mediumDark: colors.warningBorderMediumDark,
            bold: colors.warningBorderBold,
            boldDark: colors.warningBorderBoldDark,
        },
    },
    error: {
        background: {
            default: colors.errorBackgroundDefault,
            defaultDark: colors.errorBackgroundDefaultDark,
            hover: colors.errorBackgroundHover,
            hoverDark: colors.errorBackgroundHoverDark,
            active: colors.errorBackgroundActive,
            activeDark: colors.errorBackgroundActiveDark,
        },
        text: {
            medium: colors.errorTextMedium,
            mediumDark: colors.errorTextMediumDark,
            bold: colors.errorTextBold,
            boldDark: colors.errorTextBoldDark,
        },
        icon: {
            light: colors.errorIconLight,
            lightDark: colors.errorIconLightDark,
            medium: colors.errorIconMedium,
            mediumDark: colors.errorIconMediumDark,
            bold: colors.errorIconBold,
            boldDark: colors.errorIconBoldDark,
        },
        border: {
            light: colors.errorBorderLight,
            lightDark: colors.errorBorderLightDark,
            medium: colors.errorBorderMedium,
            mediumDark: colors.errorBorderMediumDark,
            bold: colors.errorBorderBold,
            boldDark: colors.errorBorderBoldDark,
        },
    },
    highlight: {
        background: {
            default: colors.highlightBackgroundDefault,
            defaultDark: colors.highlightBackgroundDefaultDark,
            hover: colors.highlightBackgroundHover,
            hoverDark: colors.highlightBackgroundHoverDark,
            active: colors.highlightBackgroundActive,
            activeDark: colors.highlightBackgroundActiveDark,
        },
        text: {
            medium: colors.highlightTextMedium,
            mediumDark: colors.highlightTextMediumDark,
            bold: colors.highlightTextBold,
            boldDark: colors.highlightTextBoldDark,
        },
        icon: {
            light: colors.highlightIconLight,
            lightDark: colors.highlightIconLightDark,
            medium: colors.highlightIconMedium,
            mediumDark: colors.highlightIconMediumDark,
            bold: colors.highlightIconBold,
            boldDark: colors.highlightIconBoldDark,
        },
        border: {
            light: colors.highlightBorderLight,
            lightDark: colors.highlightBorderLightDark,
            medium: colors.highlightBorderMedium,
            mediumDark: colors.highlightBorderMediumDark,
            bold: colors.highlightBorderBold,
            boldDark: colors.highlightBorderBoldDark,
        },
    },
    brand: {
        primary: {
            default: colors.brandPrimaryDefault,
            defaultDark: colors.brandPrimaryDefaultDark,
            hover: colors.brandPrimaryHover,
            hoverDark: colors.brandPrimaryHoverDark,
            active: colors.brandPrimaryActive,
            activeDark: colors.brandPrimaryActiveDark,
            disabled: colors.brandPrimaryDisabled,
            disabledDark: colors.brandPrimaryDisabledDark,
            onPrimary: colors.brandPrimaryOnPrimary,
            onPrimaryDark: colors.brandPrimaryOnPrimaryDark,
        },
        secondary: {
            default: colors.brandSecondaryDefault,
            defaultDark: colors.brandSecondaryDefaultDark,
            hover: colors.brandSecondaryHover,
            hoverDark: colors.brandSecondaryHoverDark,
            active: colors.brandSecondaryActive,
            activeDark: colors.brandSecondaryActiveDark,
            onSecondary: colors.brandSecondaryOnSecondary,
            onSecondaryDark: colors.brandSecondaryOnSecondaryDark,
        },
    },
    colorCoding: {
        blue: {
            background: {
                bold: colors.colorCodingBlueBackgroundBold,
                boldDark: colors.colorCodingBlueBackgroundBoldDark,
                medium: colors.colorCodingBlueBackgroundMedium,
                mediumDark: colors.colorCodingBlueBackgroundMediumDark,
                light: colors.colorCodingBlueBackgroundLight,
                lightDark: colors.colorCodingBlueBackgroundLightDark,
                thin: colors.colorCodingBlueBackgroundThin,
                thinDark: colors.colorCodingBlueBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingBlueTextOnBold,
                onBoldDark: colors.colorCodingBlueTextOnBoldDark,
                onMedium: colors.colorCodingBlueTextOnMedium,
                onMediumDark: colors.colorCodingBlueTextOnMediumDark,
                onLight: colors.colorCodingBlueTextOnLight,
                onLightDark: colors.colorCodingBlueTextOnLightDark,
                onThin: colors.colorCodingBlueTextOnThin,
                onThinDark: colors.colorCodingBlueTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingBlueIconOnBold,
                onBoldDark: colors.colorCodingBlueIconOnBoldDark,
                onMedium: colors.colorCodingBlueIconOnMedium,
                onMediumDark: colors.colorCodingBlueIconOnMediumDark,
                onLight: colors.colorCodingBlueIconOnLight,
                onLightDark: colors.colorCodingBlueIconOnLightDark,
                onThin: colors.colorCodingBlueIconOnThin,
                onThinDark: colors.colorCodingBlueIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingBlueBorderBold,
                boldDark: colors.colorCodingBlueBorderBoldDark,
                medium: colors.colorCodingBlueBorderMedium,
                mediumDark: colors.colorCodingBlueBorderMediumDark,
                light: colors.colorCodingBlueBorderLight,
                lightDark: colors.colorCodingBlueBorderLightDark,
                thin: colors.colorCodingBlueBorderThin,
                thinDark: colors.colorCodingBlueBorderThingDark,
            },
        },
        teal: {
            background: {
                bold: colors.colorCodingTealBackgroundBold,
                boldDark: colors.colorCodingTealBackgroundBoldDark,
                medium: colors.colorCodingTealBackgroundMedium,
                mediumDark: colors.colorCodingTealBackgroundMediumDark,
                light: colors.colorCodingTealBackgroundLight,
                lightDark: colors.colorCodingTealBackgroundLightDark,
                thin: colors.colorCodingTealBackgroundThin,
                thinDark: colors.colorCodingTealBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingTealTextOnBold,
                onBoldDark: colors.colorCodingTealTextOnBoldDark,
                onMedium: colors.colorCodingTealTextOnMedium,
                onMediumDark: colors.colorCodingTealTextOnMediumDark,
                onLight: colors.colorCodingTealTextOnLight,
                onLightDark: colors.colorCodingTealTextOnLightDark,
                onThin: colors.colorCodingTealTextOnThin,
                onThinDark: colors.colorCodingTealTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingTealIconOnBold,
                onBoldDark: colors.colorCodingTealIconOnBoldDark,
                onMedium: colors.colorCodingTealIconOnMedium,
                onMediumDark: colors.colorCodingTealIconOnMediumDark,
                onLight: colors.colorCodingTealIconOnLight,
                onLightDark: colors.colorCodingTealIconOnLightDark,
                onThin: colors.colorCodingTealIconOnThin,
                onThinDark: colors.colorCodingTealIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingTealBorderBold,
                boldDark: colors.colorCodingTealBorderBoldDark,
                medium: colors.colorCodingTealBorderMedium,
                mediumDark: colors.colorCodingTealBorderMediumDark,
                light: colors.colorCodingTealBorderLight,
                lightDark: colors.colorCodingTealBorderLightDark,
                thin: colors.colorCodingTealBorderThin,
                thinDark: colors.colorCodingTealBorderThingDark,
            },
        },
        green: {
            background: {
                bold: colors.colorCodingGreenBackgroundBold,
                boldDark: colors.colorCodingGreenBackgroundBoldDark,
                medium: colors.colorCodingGreenBackgroundMedium,
                mediumDark: colors.colorCodingGreenBackgroundMediumDark,
                light: colors.colorCodingGreenBackgroundLight,
                lightDark: colors.colorCodingGreenBackgroundLightDark,
                thin: colors.colorCodingGreenBackgroundThin,
                thinDark: colors.colorCodingGreenBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingGreenTextOnBold,
                onBoldDark: colors.colorCodingGreenTextOnBoldDark,
                onMedium: colors.colorCodingGreenTextOnMedium,
                onMediumDark: colors.colorCodingGreenTextOnMediumDark,
                onLight: colors.colorCodingGreenTextOnLight,
                onLightDark: colors.colorCodingGreenTextOnLightDark,
                onThin: colors.colorCodingGreenTextOnThin,
                onThinDark: colors.colorCodingGreenTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingGreenIconOnBold,
                onBoldDark: colors.colorCodingGreenIconOnBoldDark,
                onMedium: colors.colorCodingGreenIconOnMedium,
                onMediumDark: colors.colorCodingGreenIconOnMediumDark,
                onLight: colors.colorCodingGreenIconOnLight,
                onLightDark: colors.colorCodingGreenIconOnLightDark,
                onThin: colors.colorCodingGreenIconOnThin,
                onThinDark: colors.colorCodingGreenIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingGreenBorderBold,
                boldDark: colors.colorCodingGreenBorderBoldDark,
                medium: colors.colorCodingGreenBorderMedium,
                mediumDark: colors.colorCodingGreenBorderMediumDark,
                light: colors.colorCodingGreenBorderLight,
                lightDark: colors.colorCodingGreenBorderLightDark,
                thin: colors.colorCodingGreenBorderThin,
                thinDark: colors.colorCodingGreenBorderThingDark,
            },
        },
        lime: {
            background: {
                bold: colors.colorCodingLimeBackgroundBold,
                boldDark: colors.colorCodingLimeBackgroundBoldDark,
                medium: colors.colorCodingLimeBackgroundMedium,
                mediumDark: colors.colorCodingLimeBackgroundMediumDark,
                light: colors.colorCodingLimeBackgroundLight,
                lightDark: colors.colorCodingLimeBackgroundLightDark,
                thin: colors.colorCodingLimeBackgroundThin,
                thinDark: colors.colorCodingLimeBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingLimeTextOnBold,
                onBoldDark: colors.colorCodingLimeTextOnBoldDark,
                onMedium: colors.colorCodingLimeTextOnMedium,
                onMediumDark: colors.colorCodingLimeTextOnMediumDark,
                onLight: colors.colorCodingLimeTextOnLight,
                onLightDark: colors.colorCodingLimeTextOnLightDark,
                onThin: colors.colorCodingLimeTextOnThin,
                onThinDark: colors.colorCodingLimeTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingLimeIconOnBold,
                onBoldDark: colors.colorCodingLimeIconOnBoldDark,
                onMedium: colors.colorCodingLimeIconOnMedium,
                onMediumDark: colors.colorCodingLimeIconOnMediumDark,
                onLight: colors.colorCodingLimeIconOnLight,
                onLightDark: colors.colorCodingLimeIconOnLightDark,
                onThin: colors.colorCodingLimeIconOnThin,
                onThinDark: colors.colorCodingLimeIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingLimeBorderBold,
                boldDark: colors.colorCodingLimeBorderBoldDark,
                medium: colors.colorCodingLimeBorderMedium,
                mediumDark: colors.colorCodingLimeBorderMediumDark,
                light: colors.colorCodingLimeBorderLight,
                lightDark: colors.colorCodingLimeBorderLightDark,
                thin: colors.colorCodingLimeBorderThin,
                thinDark: colors.colorCodingLimeBorderThingDark,
            },
        },
        amber: {
            background: {
                bold: colors.colorCodingAmberBackgroundBold,
                boldDark: colors.colorCodingAmberBackgroundBoldDark,
                medium: colors.colorCodingAmberBackgroundMedium,
                mediumDark: colors.colorCodingAmberBackgroundMediumDark,
                light: colors.colorCodingAmberBackgroundLight,
                lightDark: colors.colorCodingAmberBackgroundLightDark,
                thin: colors.colorCodingAmberBackgroundThin,
                thinDark: colors.colorCodingAmberBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingAmberTextOnBold,
                onBoldDark: colors.colorCodingAmberTextOnBoldDark,
                onMedium: colors.colorCodingAmberTextOnMedium,
                onMediumDark: colors.colorCodingAmberTextOnMediumDark,
                onLight: colors.colorCodingAmberTextOnLight,
                onLightDark: colors.colorCodingAmberTextOnLightDark,
                onThin: colors.colorCodingAmberTextOnThin,
                onThinDark: colors.colorCodingAmberTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingAmberIconOnBold,
                onBoldDark: colors.colorCodingAmberIconOnBoldDark,
                onMedium: colors.colorCodingAmberIconOnMedium,
                onMediumDark: colors.colorCodingAmberIconOnMediumDark,
                onLight: colors.colorCodingAmberIconOnLight,
                onLightDark: colors.colorCodingAmberIconOnLightDark,
                onThin: colors.colorCodingAmberIconOnThin,
                onThinDark: colors.colorCodingAmberIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingAmberBorderBold,
                boldDark: colors.colorCodingAmberBorderBoldDark,
                medium: colors.colorCodingAmberBorderMedium,
                mediumDark: colors.colorCodingAmberBorderMediumDark,
                light: colors.colorCodingAmberBorderLight,
                lightDark: colors.colorCodingAmberBorderLightDark,
                thin: colors.colorCodingAmberBorderThin,
                thinDark: colors.colorCodingAmberBorderThingDark,
            },
        },
        orange: {
            background: {
                bold: colors.colorCodingOrangeBackgroundBold,
                boldDark: colors.colorCodingOrangeBackgroundBoldDark,
                medium: colors.colorCodingOrangeBackgroundMedium,
                mediumDark: colors.colorCodingOrangeBackgroundMediumDark,
                light: colors.colorCodingOrangeBackgroundLight,
                lightDark: colors.colorCodingOrangeBackgroundLightDark,
                thin: colors.colorCodingOrangeBackgroundThin,
                thinDark: colors.colorCodingOrangeBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingOrangeTextOnBold,
                onBoldDark: colors.colorCodingOrangeTextOnBoldDark,
                onMedium: colors.colorCodingOrangeTextOnMedium,
                onMediumDark: colors.colorCodingOrangeTextOnMediumDark,
                onLight: colors.colorCodingOrangeTextOnLight,
                onLightDark: colors.colorCodingOrangeTextOnLightDark,
                onThin: colors.colorCodingOrangeTextOnThin,
                onThinDark: colors.colorCodingOrangeTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingOrangeIconOnBold,
                onBoldDark: colors.colorCodingOrangeIconOnBoldDark,
                onMedium: colors.colorCodingOrangeIconOnMedium,
                onMediumDark: colors.colorCodingOrangeIconOnMediumDark,
                onLight: colors.colorCodingOrangeIconOnLight,
                onLightDark: colors.colorCodingOrangeIconOnLightDark,
                onThin: colors.colorCodingOrangeIconOnThin,
                onThinDark: colors.colorCodingOrangeIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingOrangeBorderBold,
                boldDark: colors.colorCodingOrangeBorderBoldDark,
                medium: colors.colorCodingOrangeBorderMedium,
                mediumDark: colors.colorCodingOrangeBorderMediumDark,
                light: colors.colorCodingOrangeBorderLight,
                lightDark: colors.colorCodingOrangeBorderLightDark,
                thin: colors.colorCodingOrangeBorderThin,
                thinDark: colors.colorCodingOrangeBorderThingDark,
            },
        },
        red: {
            background: {
                bold: colors.colorCodingRedBackgroundBold,
                boldDark: colors.colorCodingRedBackgroundBoldDark,
                medium: colors.colorCodingRedBackgroundMedium,
                mediumDark: colors.colorCodingRedBackgroundMediumDark,
                light: colors.colorCodingRedBackgroundLight,
                lightDark: colors.colorCodingRedBackgroundLightDark,
                thin: colors.colorCodingRedBackgroundThin,
                thinDark: colors.colorCodingRedBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingRedTextOnBold,
                onBoldDark: colors.colorCodingRedTextOnBoldDark,
                onMedium: colors.colorCodingRedTextOnMedium,
                onMediumDark: colors.colorCodingRedTextOnMediumDark,
                onLight: colors.colorCodingRedTextOnLight,
                onLightDark: colors.colorCodingRedTextOnLightDark,
                onThin: colors.colorCodingRedTextOnThin,
                onThinDark: colors.colorCodingRedTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingRedIconOnBold,
                onBoldDark: colors.colorCodingRedIconOnBoldDark,
                onMedium: colors.colorCodingRedIconOnMedium,
                onMediumDark: colors.colorCodingRedIconOnMediumDark,
                onLight: colors.colorCodingRedIconOnLight,
                onLightDark: colors.colorCodingRedIconOnLightDark,
                onThin: colors.colorCodingRedIconOnThin,
                onThinDark: colors.colorCodingRedIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingRedBorderBold,
                boldDark: colors.colorCodingRedBorderBoldDark,
                medium: colors.colorCodingRedBorderMedium,
                mediumDark: colors.colorCodingRedBorderMediumDark,
                light: colors.colorCodingRedBorderLight,
                lightDark: colors.colorCodingRedBorderLightDark,
                thin: colors.colorCodingRedBorderThin,
                thinDark: colors.colorCodingRedBorderThingDark,
            },
        },
        pink: {
            background: {
                bold: colors.colorCodingPinkBackgroundBold,
                boldDark: colors.colorCodingPinkBackgroundBoldDark,
                medium: colors.colorCodingPinkBackgroundMedium,
                mediumDark: colors.colorCodingPinkBackgroundMediumDark,
                light: colors.colorCodingPinkBackgroundLight,
                lightDark: colors.colorCodingPinkBackgroundLightDark,
                thin: colors.colorCodingPinkBackgroundThin,
                thinDark: colors.colorCodingPinkBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingPinkTextOnBold,
                onBoldDark: colors.colorCodingPinkTextOnBoldDark,
                onMedium: colors.colorCodingPinkTextOnMedium,
                onMediumDark: colors.colorCodingPinkTextOnMediumDark,
                onLight: colors.colorCodingPinkTextOnLight,
                onLightDark: colors.colorCodingPinkTextOnLightDark,
                onThin: colors.colorCodingPinkTextOnThin,
                onThinDark: colors.colorCodingPinkTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingPinkIconOnBold,
                onBoldDark: colors.colorCodingPinkIconOnBoldDark,
                onMedium: colors.colorCodingPinkIconOnMedium,
                onMediumDark: colors.colorCodingPinkIconOnMediumDark,
                onLight: colors.colorCodingPinkIconOnLight,
                onLightDark: colors.colorCodingPinkIconOnLightDark,
                onThin: colors.colorCodingPinkIconOnThin,
                onThinDark: colors.colorCodingPinkIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingPinkBorderBold,
                boldDark: colors.colorCodingPinkBorderBoldDark,
                medium: colors.colorCodingPinkBorderMedium,
                mediumDark: colors.colorCodingPinkBorderMediumDark,
                light: colors.colorCodingPinkBorderLight,
                lightDark: colors.colorCodingPinkBorderLightDark,
                thin: colors.colorCodingPinkBorderThin,
                thinDark: colors.colorCodingPinkBorderThingDark,
            },
        },
        purple: {
            background: {
                bold: colors.colorCodingPurpleBackgroundBold,
                boldDark: colors.colorCodingPurpleBackgroundBoldDark,
                medium: colors.colorCodingPurpleBackgroundMedium,
                mediumDark: colors.colorCodingPurpleBackgroundMediumDark,
                light: colors.colorCodingPurpleBackgroundLight,
                lightDark: colors.colorCodingPurpleBackgroundLightDark,
                thin: colors.colorCodingPurpleBackgroundThin,
                thinDark: colors.colorCodingPurpleBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingPurpleTextOnBold,
                onBoldDark: colors.colorCodingPurpleTextOnBoldDark,
                onMedium: colors.colorCodingPurpleTextOnMedium,
                onMediumDark: colors.colorCodingPurpleTextOnMediumDark,
                onLight: colors.colorCodingPurpleTextOnLight,
                onLightDark: colors.colorCodingPurpleTextOnLightDark,
                onThin: colors.colorCodingPurpleTextOnThin,
                onThinDark: colors.colorCodingPurpleTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingPurpleIconOnBold,
                onBoldDark: colors.colorCodingPurpleIconOnBoldDark,
                onMedium: colors.colorCodingPurpleIconOnMedium,
                onMediumDark: colors.colorCodingPurpleIconOnMediumDark,
                onLight: colors.colorCodingPurpleIconOnLight,
                onLightDark: colors.colorCodingPurpleIconOnLightDark,
                onThin: colors.colorCodingPurpleIconOnThin,
                onThinDark: colors.colorCodingPurpleIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingPurpleBorderBold,
                boldDark: colors.colorCodingPurpleBorderBoldDark,
                medium: colors.colorCodingPurpleBorderMedium,
                mediumDark: colors.colorCodingPurpleBorderMediumDark,
                light: colors.colorCodingPurpleBorderLight,
                lightDark: colors.colorCodingPurpleBorderLightDark,
                thin: colors.colorCodingPurpleBorderThin,
                thinDark: colors.colorCodingPurpleBorderThingDark,
            },
        },
        blueGrey: {
            background: {
                bold: colors.colorCodingBlueGreyBackgroundBold,
                boldDark: colors.colorCodingBlueGreyBackgroundBoldDark,
                medium: colors.colorCodingBlueGreyBackgroundMedium,
                mediumDark: colors.colorCodingBlueGreyBackgroundMediumDark,
                light: colors.colorCodingBlueGreyBackgroundLight,
                lightDark: colors.colorCodingBlueGreyBackgroundLightDark,
                thin: colors.colorCodingBlueGreyBackgroundThin,
                thinDark: colors.colorCodingBlueGreyBackgroundThinDark,
            },
            text: {
                onBold: colors.colorCodingBlueGreyTextOnBold,
                onBoldDark: colors.colorCodingBlueGreyTextOnBoldDark,
                onMedium: colors.colorCodingBlueGreyTextOnMedium,
                onMediumDark: colors.colorCodingBlueGreyTextOnMediumDark,
                onLight: colors.colorCodingBlueGreyTextOnLight,
                onLightDark: colors.colorCodingBlueGreyTextOnLightDark,
                onThin: colors.colorCodingBlueGreyTextOnThin,
                onThinDark: colors.colorCodingBlueGreyTextOnThinDark,
            },
            icon: {
                onBold: colors.colorCodingBlueGreyIconOnBold,
                onBoldDark: colors.colorCodingBlueGreyIconOnBoldDark,
                onMedium: colors.colorCodingBlueGreyIconOnMedium,
                onMediumDark: colors.colorCodingBlueGreyIconOnMediumDark,
                onLight: colors.colorCodingBlueGreyIconOnLight,
                onLightDark: colors.colorCodingBlueGreyIconOnLightDark,
                onThin: colors.colorCodingBlueGreyIconOnThin,
                onThinDark: colors.colorCodingBlueGreyIconOnThinDark,
            },
            border: {
                bold: colors.colorCodingBlueGreyBorderBold,
                boldDark: colors.colorCodingBlueGreyBorderBoldDark,
                medium: colors.colorCodingBlueGreyBorderMedium,
                mediumDark: colors.colorCodingBlueGreyBorderMediumDark,
                light: colors.colorCodingBlueGreyBorderLight,
                lightDark: colors.colorCodingBlueGreyBorderLightDark,
                thin: colors.colorCodingBlueGreyBorderThin,
                thinDark: colors.colorCodingBlueGreyBorderThingDark,
            },
        },
    },
} as const;
